import type { FC } from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Formik } from 'formik';
import { TextField, Grid, Button, Alert } from '@material-ui/core';
import * as Xlsx from 'xlsx';
import { connect } from 'react-redux';
import Panel from '../../../components/global/Panel';
import PanelHeader from '../../../components/global/PanelHeader';
import PanelTitle from '../../../components/global/PanelTitle';
import PanelContent from '../../../components/global/PanelContent';
import { months } from '../../../utils/data/months';
import {
  startLoading,
  stopLoading,
  onPost,
} from '../../../store/actions/global';
import useIsMountedRef from '../../../hooks/useIsMountedRef';

const Upload: FC<any> = ({
  startLoading: startLoadingExec,
  stopLoading: stopLoadingExec,
  onPost: onPostExec,
}) => {
  const isMountedRef = useIsMountedRef();
  const title = 'Carga de Datos';
  const currentDate = new Date();
  const [dataFile, setDataFile] = useState([]);
  const [dataRelation, setDataRelation] = useState([]);
  const [dataOffice, setDataOffice] = useState([]);
  const [dataRelationOffice, setDataRelationOffice] = useState([]);
  const [dataChannel, setDataChannel] = useState([]);
  const [dataFuvex, setDataFuvex] = useState([]);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const validate = (yearInput: number, monthInput: number): boolean => {
    setMessage('');
    if (`${yearInput}`.length !== 4) {
      setMessage('Ingrese el ańo correctamente');
      return false;
    }
    if (monthInput <= 0 || monthInput > 12) {
      setMessage('Seleccione el mes correctamente');
      return false;
    }
    if (!dataFile || dataFile.length === 0) {
      setMessage('Seleccione el archivo a cargar');
      return false;
    }
    return true;
  };

  const submitFile = async (values: { year: number; month: number }) => {
    if (isMountedRef.current) {
      startLoadingExec();
      if (!validate(values.year, values.month)) {
        stopLoadingExec();
        return;
      }
      const response = await onPostExec({
        url: 'admin/upload',
        body: {
          year: values.year,
          month: values.month,
          meta: dataFile,
          relation: dataRelation,
          office: dataOffice,
          relationOffice: dataRelationOffice,
          channel: dataChannel,
          fuvex: dataFuvex
        },
      });
      if (!response) {
        setMessage('No se guardaron los datos');
      } else {
        setMessage('Se guardaron los datos correctamente');
        setSuccess(true);
        setDataRelation([]);
        setDataFile([]);
        setDataRelationOffice([]);
        setDataChannel([]);
        setDataFuvex([]);
        setTimeout(() => {
          setMessage('');
          setSuccess(false);
        }, 5000);
      }
    }
  };

  const readFile = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    startLoadingExec();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = Xlsx.read(bstr, { type: 'binary' });
      const [
        sheetJfnName,
        sheetRelationName,
        sheetOficinaName,
        sheetRelationOfficeName,
        sheetChannel,
        sheetFuvex,
      ] = wb.SheetNames;
      const dataJfn = Xlsx.utils.sheet_to_json(wb.Sheets[sheetJfnName]);
      const dataRel = Xlsx.utils.sheet_to_json(wb.Sheets[sheetRelationName]);
      const dataOffi = Xlsx.utils.sheet_to_json(wb.Sheets[sheetOficinaName]);
      const dataRelOffi = Xlsx.utils.sheet_to_json(
        wb.Sheets[sheetRelationOfficeName],
      );
      const dataLoadChannel = Xlsx.utils.sheet_to_json(wb.Sheets[sheetChannel]);
      const dataLoadFuvex = Xlsx.utils.sheet_to_json(wb.Sheets[sheetFuvex]);
      setDataFile(dataJfn);
      setDataRelation(dataRel);
      setDataOffice(dataOffi);
      setDataRelationOffice(dataRelOffi);
      setDataChannel(dataLoadChannel);
      setDataFuvex(dataLoadFuvex);
      stopLoadingExec();
    };
    reader.readAsBinaryString(file);
  };

  return (
    <>
      <Helmet>
        <title>
          Dashboard:
          {title}
        </title>
      </Helmet>
      <Panel>
        <PanelHeader>
          <PanelTitle
            title={title}
            subTitlePrim="Administración"
            subTitleSec={title}
          />
        </PanelHeader>
        <Grid container>
          <Grid item md={8} xl={6}>
            <PanelContent>
              <Formik
                initialValues={{
                  year: currentDate.getFullYear(),
                  month: currentDate.getMonth() + 1,
                }}
                onSubmit={(values) => {
                  submitFile({
                    year: values.year,
                    month: Number(values.month),
                  });
                }}
              >
                {({ values, handleChange, handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item md={6} xs={12}>
                        <TextField
                          type="number"
                          name="year"
                          size="small"
                          label="Ańo:"
                          fullWidth
                          value={values.year}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <TextField
                          name="month"
                          size="small"
                          label="Mes:"
                          type="number"
                          fullWidth
                          select
                          SelectProps={{ native: true }}
                          value={values.month}
                          onChange={handleChange}
                        >
                          {months.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          type="file"
                          onChange={readFile}
                        />
                      </Grid>
                      <Grid item md={12}>
                        <Button variant="contained" type="submit">
                          Cargar Archivo
                        </Button>
                      </Grid>
                      {message && (
                        <Grid item md={12}>
                          <Alert severity={success ? 'info' : 'error'}>
                            {message}
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </form>
                )}
              </Formik>
            </PanelContent>
          </Grid>
        </Grid>
      </Panel>
    </>
  );
};

export default connect(null, {
  startLoading,
  stopLoading,
  onPost,
})(Upload);
